export class LiveDomUpdater {
  public updateWidgetField(widgetId: string, key: string, value: unknown): boolean {
    const block = document.querySelector<HTMLElement>(`[data-content-item-id="${widgetId}"]`);
    if (!block) return false;

    const strVal = value !== null && value !== undefined ? String(value) : '';

    // 1. Check explicit data-field attribute first
    const explicitTarget = block.querySelector<HTMLElement>(`[data-field="${key}"]`);
    if (explicitTarget) {
      this.applyValue(explicitTarget, key, strVal);
      return true;
    }

    const lowerKey = key.toLowerCase();

    // Direct anchor updates (e.g. LinkMenuItem)
    if (block instanceof HTMLAnchorElement) {
      if (lowerKey === 'name' || lowerKey === 'displaytext' || lowerKey === 'title') {
        block.textContent = strVal;
        return true;
      }
      if (lowerKey === 'url' || lowerKey === 'href') {
        block.href = strVal;
        return true;
      }
    }

    // 2. Headlines, Headings, Titles
    if (lowerKey.includes('headline') || lowerKey.includes('heading') || lowerKey === 'title') {
      const heading = block.querySelector<HTMLElement>(
        'h1, h2, h3, .hero-title, .section-heading, .title'
      );
      if (heading) {
        heading.textContent = strVal;
        return true;
      }
    }

    // 3. Subheadings, Lead, Eyebrows
    if (
      lowerKey.includes('subheading') ||
      lowerKey.includes('lead') ||
      lowerKey.includes('subtitle')
    ) {
      const lead = block.querySelector<HTMLElement>(
        'p.lead, .hero-lead, .subheading, .subtitle, h3'
      );
      if (lead) {
        lead.textContent = strVal;
        return true;
      }
    }

    if (lowerKey.includes('eyebrow')) {
      const eyebrow = block.querySelector<HTMLElement>(
        '.eyebrow, [class*="eyebrow"], .telemetry-tag'
      );
      if (eyebrow) {
        eyebrow.textContent = strVal;
        return true;
      }
    }

    // 4. Body Copy, Markdown, Description
    if (
      lowerKey.includes('body') ||
      lowerKey.includes('description') ||
      lowerKey.includes('content')
    ) {
      const body = block.querySelector<HTMLElement>(
        '.body-text, .prose p, .content-desc, p:not(.lead)'
      );
      if (body) {
        body.textContent = strVal;
        return true;
      }
    }

    // 5. Button Text & Links
    if (lowerKey.includes('ctatext') || lowerKey.includes('buttontext')) {
      const btn = block.querySelector<HTMLElement>(
        'a.btn, a[class*="btn"], button, [class*="cta-button"]'
      );
      if (btn) {
        btn.textContent = strVal;
        return true;
      }
    }

    if (
      lowerKey.includes('ctaurl') ||
      lowerKey.includes('buttonurl') ||
      lowerKey.includes('link')
    ) {
      const link = block.querySelector<HTMLAnchorElement>('a.btn, a[class*="btn"], a');
      if (link && link instanceof HTMLAnchorElement) {
        link.href = strVal;
        return true;
      }
    }

    // 5b. Sign In / Auth Toggles (ShowSignIn)
    if (lowerKey === 'showsignin' || lowerKey === 'enablesignin') {
      const boolVal = value === true || strVal.toLowerCase() === 'true';
      const signInLinks = block.querySelectorAll<HTMLElement>(
        '[data-auth-sign-in], a[href*="/Login"], a[href*="login"]'
      );
      if (signInLinks.length > 0) {
        signInLinks.forEach((link) => {
          link.style.display = boolVal ? '' : 'none';
        });
        return true;
      }
    }

    // 5c. Favicon updates
    if (lowerKey.includes('favicon')) {
      block.setAttribute('data-favicon', strVal);
      const favicons = document.querySelectorAll<HTMLLinkElement>(
        'link[data-nav-favicon], link[rel*="icon"]'
      );
      if (favicons.length > 0) {
        favicons.forEach((fav) => {
          if (strVal) {
            fav.href = strVal;
          }
        });
      } else if (strVal) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.setAttribute('data-nav-favicon', 'true');
        link.href = strVal;
        document.head.appendChild(link);
      }
      const faviconImg = block.querySelector<HTMLImageElement>(
        '[data-favicon-mark], img[data-field="Favicon"]'
      );
      if (faviconImg) {
        faviconImg.src = strVal;
        faviconImg.style.display = strVal ? '' : 'none';
      }
      return true;
    }

    // 6. Background Images and Media Images
    if (
      lowerKey.includes('image') ||
      lowerKey.includes('photo') ||
      lowerKey.includes('bg') ||
      lowerKey.includes('logo')
    ) {
      if (lowerKey.includes('bg') || lowerKey.includes('background')) {
        const bgEl = block.querySelector<HTMLElement>(
          '.bg-cover, .bg-center, [style*="background-image"], .hero-bg'
        );
        if (bgEl) {
          bgEl.style.backgroundImage = strVal ? `url("${strVal}")` : '';
          return true;
        }
      }

      if (lowerKey.includes('logo')) {
        const logoImg = block.querySelector<HTMLImageElement>(
          'img[data-footer-logo], [data-footer-logo], img[data-field="Logo"], img[data-field="LogoDark"], img[data-field="LogoLight"], img[data-nav-logo]'
        );
        const fallback = block.querySelector<HTMLElement>(
          '[data-footer-logo-fallback], [data-logo-fallback]'
        );
        if (logoImg && logoImg instanceof HTMLImageElement) {
          logoImg.src = strVal;
          logoImg.style.display = strVal ? '' : 'none';
          if (fallback) {
            fallback.style.display = strVal ? 'none' : '';
          }
          return true;
        }
        if (!logoImg && strVal && fallback && fallback.parentElement) {
          const newImg = document.createElement('img');
          newImg.src = strVal;
          newImg.className = 'h-8 w-auto object-contain';
          newImg.setAttribute('data-footer-logo', '');
          fallback.parentElement.insertBefore(newImg, fallback);
          fallback.style.display = 'none';
          return true;
        }
      }

      const img = block.querySelector<HTMLImageElement>('img');
      if (img && img instanceof HTMLImageElement) {
        img.src = strVal;
        if (!strVal) {
          img.style.display = 'none';
        } else {
          img.style.display = '';
        }
        return true;
      }

      const bgEl = block.querySelector<HTMLElement>(
        '.bg-cover, .bg-center, [style*="background-image"], .hero-bg'
      );
      if (bgEl) {
        bgEl.style.backgroundImage = strVal ? `url("${strVal}")` : '';
        return true;
      }

      if (
        block.style.backgroundImage ||
        block.classList.contains('bg-cover') ||
        block.classList.contains('bg-center')
      ) {
        block.style.backgroundImage = strVal ? `url("${strVal}")` : '';
        return true;
      }
    }

    // 7. Stat counters and numbers
    if (typeof value === 'number' || !isNaN(Number(strVal))) {
      const counter = block.querySelector<HTMLElement>(
        '[data-counter-value], .stat-value, .metric-number'
      );
      if (counter) {
        counter.textContent = strVal;
        if (counter.hasAttribute('data-counter-value')) {
          counter.setAttribute('data-counter-value', strVal);
        }
        return true;
      }
    }

    return false;
  }

  private applyValue(element: HTMLElement, key: string, value: string): void {
    const lowerKey = key.toLowerCase();
    if (element instanceof HTMLImageElement) {
      element.src = value;
      if (!value) {
        element.style.display = 'none';
      } else {
        element.style.display = '';
      }
      if (lowerKey.includes('logo') && element.parentElement) {
        const fallback = element.parentElement.querySelector<HTMLElement>(
          '[data-footer-logo-fallback], [data-logo-fallback]'
        );
        if (fallback) {
          fallback.style.display = value ? 'none' : '';
        }
      }
    } else if (
      element instanceof HTMLAnchorElement &&
      (lowerKey.includes('url') || lowerKey.includes('href'))
    ) {
      element.href = value;
    } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      element.value = value;
    } else {
      element.textContent = value;
    }
  }
}

export const liveDomUpdater = new LiveDomUpdater();
